import moment from "moment";
import { vehicleOf } from "./introCamera";
import { spread } from "./introDrawing";
import { glyphFor } from "./puffyGlyphs";
import { isVideoMedia, ReelMediaItem } from "./reelMedia";

/**
 * What the passport intros stamp, and when. Everything here is a pure function of the trip and the
 * frame, so the two passport styles in PassportIntro.tsx only draw.
 */

export type StampShape = "circle" | "rect" | "octagon";

export type PassportStamp = {
	/** Which stop of the trip it is, from 0. */
	stop: number;
	/** The stop's name as the reel labels it everywhere else. */
	city: string;
	/** The date the stop starts on, "06 FEB" -- or "DAY 3" for a template, which has no dates. */
	when: string;
	/** "3 NIGHTS", "1 NIGHT", or "DAY STOP" for a stop passed through. */
	nights: string;
	/** The Puffy glyph of how you arrived: the vehicle of the leg before. Null on the first stop, which says ENTRY. */
	arrivedBy: string | null;
	ink: string;
	shape: StampShape;
};

/** Saturated inks, so a stamp reads on paper and on a blurred map. Cycled per stop. */
export const STAMP_INKS = ["#e84d7e", "#0f3e4a", "#0b8f68", "#ff6a2b", "#3355d6", "#7a3d9a", "#d62f2f"];
const SHAPES: StampShape[] = ["circle", "rect", "octagon"];

const nightsLabel = (n: number) => (n <= 0 ? "DAY STOP" : n === 1 ? "1 NIGHT" : `${n} NIGHTS`);

/**
 * One stamp per stop, in order. The day a stop starts on is its own `firstDayNumber` when the trip has
 * one, and otherwise the nights before it added up.
 */
export const passportStamps = (reelData: any[], currentTrip: any): PassportStamp[] => {
	const stops = reelData || [];
	const template = Boolean(currentTrip?.isTemplate || currentTrip?.stops === "places");
	// moment.utc(undefined) is now, and valid: a trip without a start must not be stamped with today.
	const start = moment.utc(currentTrip?.startDate ?? null);
	let day = 1;
	return stops.map((s, i) => {
		const firstDay = Number(s?.firstDayNumber) || day;
		day = firstDay + (Number(s?.duration) || 0);
		const when =
			template || !start.isValid()
				? `DAY ${firstDay}`
				: start
						.clone()
						.add(firstDay - 1, "days")
						.format("DD MMM")
						.toUpperCase();
		return {
			stop: i,
			city: String(s?.city || "").toUpperCase(),
			when,
			nights: nightsLabel(Number(s?.duration) || 0),
			arrivedBy: i === 0 ? null : glyphFor(vehicleOf(stops[i - 1])),
			ink: STAMP_INKS[i % STAMP_INKS.length],
			shape: SHAPES[i % SHAPES.length],
		};
	});
};

/** At most `max` of them, evenly through the trip, always the first and the last. */
export const pickEvenly = <T>(all: T[], max: number): T[] => spread(all.length, max).map((i) => all[i]);

/**
 * The frames the stamps land on: evenly through `window`, at least `every[0]` apart so each one is
 * seen, and at most `every[1]` so a short trip keeps a rhythm rather than stamping in slow motion.
 */
export const stampHits = (n: number, window: number[], every: number[]): number[] => {
	if (n <= 0) return [];
	const gap = n === 1 ? every[1] : Math.max(every[0], Math.min(every[1], Math.floor((window[1] - window[0]) / (n - 1))));
	return Array.from({ length: n }, (_, j) => window[0] + j * gap);
};

/**
 * A stamp coming down: big and faint for three frames, the hit a little under size, a bounce, then
 * still. Null before it is in the air.
 */
export const slamAt = (frame: number, at: number): { scale: number; opacity: number } | null => {
	if (frame < at - 3) return null;
	if (frame < at) return { scale: 1.9 - (frame - (at - 3)) * 0.28, opacity: 0.22 };
	const t = frame - at;
	return { scale: t === 0 ? 0.93 : t === 1 ? 1.04 : t === 2 ? 0.99 : 1, opacity: 1 };
};

/** The knock of every hit on what it lands on: five frames, alternating, dying away. */
export const shakeAt = (frame: number, hits: number[], amplitude: number): [number, number] =>
	hits.reduce<[number, number]>(
		([x, y], h) => {
			const t = frame - h;
			if (t < 0 || t >= 5) return [x, y];
			const a = amplitude * (1 - t / 5);
			return [x + (t % 2 ? -0.6 : 0.6) * a, y + (t % 2 ? 1 : -1) * a];
		},
		[0, 0]
	);

/**
 * Stacking for the passport's leaves, the cover being leaf -1. Unturned leaves lie on the right with
 * the next one to turn on top; turned ones pile up on the left, the latest on top; a leaf in motion is
 * above both.
 */
export const leafZ = (order: number, turned: number) => (turned > 0 && turned < 1 ? 300 : turned >= 1 ? 100 + order : 50 - order);

/** The photo each stop gives the montage -- its first still -- for at most `max` stops, first and last kept. */
export const montageShots = (reelData: any[], max: number): { stop: number; photo: ReelMediaItem }[] =>
	pickEvenly(
		(reelData || [])
			.map((s, stop) => ({ stop, photo: ((s?.photos || []) as ReelMediaItem[]).find((m) => !isVideoMedia(m)) }))
			.filter((s): s is { stop: number; photo: ReelMediaItem } => Boolean(s.photo)),
		max
	);
